import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Tab,
  Tabs,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Tag as TagIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import config from '../../config';
import './TaggedUsers.css';
import { normalizeImageUrl } from '../../components/Post';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const TaggedUsers = () => {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // 検索とページネーション
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // タグ付けダイアログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUserFormData, setNewUserFormData] = useState({
    username: '',
    email: '',
    tags: []
  });
  const [inputTag, setInputTag] = useState('');
  
  // タグ削除確認ダイアログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // ユーザープロフィール編集ダイアログ
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    displayName: '',
    email: '',
    bio: '',
    location: '',
    website: ''
  });
  
  // 画像編集用の状態
  const [avatarPreview, setAvatarPreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  
  // フォロー状態の管理
  const [followStatus, setFollowStatus] = useState({});
  
  // 投稿ユーザー情報編集用の状態
  const [isPosterUser, setIsPosterUser] = useState(false);
  const [posterProfile, setPosterProfile] = useState({
    shop_name: '',
    prefecture_code: '',
    city_code: '',
    stage_name: '',
    age: '',
    height: '',
    bust: '',
    cup_size: '',
    waist: '',
    hip: ''
  });
  
  // カップサイズの選択肢
  const CUP_SIZES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
  
  // 地域データ用の状態
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  
  // ユーザープロフィール表示ダイアログ
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [userToView, setUserToView] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // 画像アップロード関数を追加
  const uploadImage = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await axios.post(`${config.API_BASE_URL}/api/profiles/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.image_url) {
        return response.data.image_url;
      } else {
        throw new Error('画像URLが返されませんでした');
      }
    } catch (error) {
      console.error('画像アップロードエラー:', error);
      return null;
    }
  };
  
  // タグ付け関係の確認
  const checkTagRelationship = async (username) => {
    try {
      // 現在の企業ユーザーとタグ付けユーザーの関係を確認
      
      const response = await axios.get(`${config.API_BASE_URL}/api/corporate/check-tag-relationship/${username}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      
      return response.data.hasRelationship;
    } catch (error) {
      
      return false;
    }
  };
  
  // ユーザーのフォロー状態を確認
  const checkFollowStatus = async (username) => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/social/is_following/${username}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data.is_following === true;
    } catch (err) {
      
      return false;
    }
  };
  
  // フォロー/フォロー解除処理
  const handleFollowToggle = async (userId, username) => {
    try {
      const isCurrentlyFollowing = followStatus[userId];
      
      // 楽観的UIアップデート
      setFollowStatus({
        ...followStatus,
        [userId]: !isCurrentlyFollowing
      });
      
      if (isCurrentlyFollowing) {
        // フォロー解除API呼び出し
        await axios.post(`${config.API_BASE_URL}/api/social/unfollow/${username}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        // フォローAPI呼び出し
        await axios.post(`${config.API_BASE_URL}/api/social/follow/${username}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // 操作後にフォロー状態をAPIで再確認
      try {
        const actualFollowStatus = await checkFollowStatus(username);
        setFollowStatus(prevStatus => ({
          ...prevStatus,
          [userId]: actualFollowStatus
        }));
      } catch (statusError) {
        
      }
    } catch (err) {
      
      // エラー時は元の状態に戻す
      setFollowStatus({
        ...followStatus,
        [userId]: !followStatus[userId]
      });
      setError('フォロー処理中にエラーが発生しました。');
    }
  };
  
  // モックユーザーデータの生成（開発用）
  const generateMockUsers = () => {
    const tags = ['VIP', 'プレミアム会員', '法人', '個人', 'リード', '見込み客', '契約済み'];
    
    return Array(35).fill().map((_, i) => ({
      id: `user-${i+1}`,
      username: `ユーザー${i+1}`,
      displayName: `表示名 ${i+1}`,
      email: `user${i+1}@example.com`,
      avatar: `https://i.pravatar.cc/150?img=${i+1}`,
      tags: Array(Math.floor(Math.random() * 3) + 1)
        .fill()
        .map(() => tags[Math.floor(Math.random() * tags.length)]),
      addedAt: new Date(Date.now() - Math.floor(Math.random() * 30 * 24 * 60 * 60 * 1000)).toISOString()
    }));
  };
  
  // 検索処理
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(taggedUsers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = taggedUsers.filter(user => 
        user.username.toLowerCase().includes(term) ||
        user.displayName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.tags.some(tag => tag.toLowerCase().includes(term))
      );
      setFilteredUsers(filtered);
    }
    setPage(0);
  }, [searchTerm, taggedUsers]);
  
  // 検索フィールドの変更処理
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // ページネーション処理
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // タグ付けダイアログ処理
  const handleOpenTagDialog = () => {
    setNewUserFormData({
      username: '',
      email: '',
      tags: []
    });
    setInputTag('');
    setDialogOpen(true);
  };
  
  const handleCloseTagDialog = () => {
    setDialogOpen(false);
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewUserFormData({
      ...newUserFormData,
      [name]: value
    });
  };
  
  const handleInputTagChange = (e) => {
    setInputTag(e.target.value);
  };
  
  const handleAddTag = () => {
    if (inputTag.trim() !== '' && !newUserFormData.tags.includes(inputTag.trim())) {
      setNewUserFormData({
        ...newUserFormData,
        tags: [...newUserFormData.tags, inputTag.trim()]
      });
      setInputTag('');
    }
  };
  
  const handleDeleteTag = (tagToDelete) => {
    setNewUserFormData({
      ...newUserFormData,
      tags: newUserFormData.tags.filter(tag => tag !== tagToDelete)
    });
  };
  
  // 新規ユーザー登録処理
  const handleSubmitNewUser = async () => {
    // バリデーション
    if (!newUserFormData.username || !newUserFormData.email || newUserFormData.tags.length === 0) {
      setError('すべての必須項目を入力してください。');
      return;
    }
    
    try {
      setLoading(true);
      
      await axios.post(`${config.API_BASE_URL}/api/corporate/tagged-users`, newUserFormData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // 最新のユーザーリストを再取得
      const response = await axios.get(`${config.API_BASE_URL}/api/corporate/tagged-users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTaggedUsers(response.data.users || []);
      setFilteredUsers(response.data.users || []);
      
      setDialogOpen(false);
      setLoading(false);
    } catch (err) {
      
      setError('ユーザーの追加中にエラーが発生しました。');
      setLoading(false);
      
      // モック用の処理（開発用）
      const newUser = {
        id: `user-${Date.now()}`,
        username: newUserFormData.username,
        displayName: newUserFormData.username,
        email: newUserFormData.email,
        avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70)}`,
        tags: [...newUserFormData.tags],
        addedAt: new Date().toISOString()
      };
      
      setTaggedUsers([...taggedUsers, newUser]);
      setFilteredUsers([...taggedUsers, newUser]);
      
      setDialogOpen(false);
    }
  };
  
  // ユーザープロフィール編集ダイアログ
  const handleOpenEditDialog = (user) => {
    setUserToEdit(user);
    setActiveTab(0); // 基本情報タブをアクティブにする
    setError(null); // エラーメッセージをクリア

    // 初期値を設定
    const initialEditForm = {
      username: user.username || '',
      displayName: user.displayName || '',
      email: user.email || '',
      bio: '',
      location: '',
      website: ''
    };

    setEditUserForm(initialEditForm);

    // 初期の画像URLを設定
    // base64形式のデータも許可
    if (user.avatar) {
      setAvatarPreview(user.avatar);
    } else {
      setAvatarPreview('');
    }
    setCoverPreview('');

    // デバッグログ
    console.log('編集ダイアログを開始:', {
      userId: user.id,
      username: user.username,
      initialForm: initialEditForm,
      avatarPreview: user.avatar ? (user.avatar.startsWith('data:image') ? '(base64データ)' : user.avatar) : 'なし'
    });

    // タグ付け関係を確認
    checkTagRelationship(user.username)
      .then(hasRelationship => {
        if (!hasRelationship) {
          
          // この時点でトーストなどで警告を表示することも可能
        }
      });

    // プロフィール詳細情報を取得
    const fetchUserDetails = async () => {
      try {
        setProfileLoading(true);
        

        const response = await axios.get(`${config.API_BASE_URL}/api/users/${user.username}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const userData = response.data;
        
        
        // 基本プロフィール情報をセット
        setEditUserForm({
          username: userData.username || user.username || '',
          displayName: userData.display_name || user.displayName || '',
          email: userData.email || user.email || '',
          bio: userData.bio || '',
          location: userData.location || '',
          website: userData.website || ''
        });

        // アバターとカバー画像の設定
        // base64形式のデータでも表示できるように修正
        if (userData.avatar) {
          setAvatarPreview(userData.avatar);
        }

        if (userData.cover_image) {
          setCoverPreview(userData.cover_image);
        }
        
        // 投稿ユーザー情報があればセット
        if (userData.poster_profile) {
          setIsPosterUser(true);
          const profile = userData.poster_profile;
          
          setPosterProfile({
            shop_name: profile.shop_name || '',
            prefecture_code: profile.prefecture?.code || '',
            city_code: profile.city?.code || '',
            stage_name: profile.stage_name || '',
            age: profile.age || '',
            height: profile.height || '',
            bust: profile.bust || '',
            cup_size: profile.cup_size || '',
            waist: profile.waist || '',
            hip: profile.hip || ''
          });
          
          // 都道府県が選択されている場合は市区町村を読み込む
          if (profile.prefecture?.code) {
            fetchCities(profile.prefecture.code);
          }
        } else {
          setIsPosterUser(false);
          setPosterProfile({
            shop_name: '',
            prefecture_code: '',
            city_code: '',
            stage_name: '',
            age: '',
            height: '',
            bust: '',
            cup_size: '',
            waist: '',
            hip: ''
          });
        }
        
        setProfileLoading(false);
      } catch (err) {
        
        
        
        // エラー時はダイアログで表示済みの情報だけ使用
        setEditUserForm({
          username: user.username || '',
          displayName: user.displayName || '',
          email: user.email || '',
          bio: '',
          location: '',
          website: ''
        });
        
        setAvatarPreview(user.avatar || '');
        setCoverPreview('');
        setIsPosterUser(false);
        setProfileLoading(false);
      }
    };
    
    fetchUserDetails();
    setEditUserDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setEditUserDialogOpen(false);
    setUserToEdit(null);
    setIsPosterUser(false);
    setAvatarPreview('');
    setCoverPreview('');
  };
  
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditUserForm({
      ...editUserForm,
      [name]: value
    });
  };
  
  // 投稿ユーザープロフィール入力の変更を処理
  const handlePosterInputChange = (e) => {
    const { name, value } = e.target;
    
    // 都道府県が変更された場合の特別処理
    if (name === 'prefecture_code') {
      // 都道府県変更時に市区町村をクリアして、新しい市区町村リストを取得
      setPosterProfile(prev => ({
        ...prev,
        [name]: value,
        city_code: '' // 市区町村をリセット
      }));
      
      // 新しい市区町村リストを取得
      fetchCities(value);
    } else {
      // 通常の入力値変更処理
      setPosterProfile(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // 数値フィールドの検証
  const validateNumberField = (value, min, max) => {
    if (!value) return true; // 空の値は許可
    
    const numValue = Number(value);
    return !isNaN(numValue) && numValue >= min && numValue <= max;
  };
  
  // ユーザー編集の送信処理
  const handleSubmitEditUser = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 現在のユーザー情報をログ出力
      console.log('現在のユーザー情報:', currentUser);
      
      // タグ付け関係を確認
      console.log(`編集対象ユーザー: ${userToEdit.username} (ID: ${userToEdit.id})`);
      const hasRelationship = await checkTagRelationship(userToEdit.username);
      
      if (!hasRelationship) {
        setError(`このユーザー(${userToEdit.username})を編集する権限がありません。タグ付け関係が見つかりません。`);
        setLoading(false);
        
        // エラーメッセージをトースト表示
        toast.error(`ユーザー「${userToEdit.username}」を編集する権限がありません。このユーザーをタグ付けしたことを確認してください。`, {
          position: 'top-center',
          autoClose: 5000
        });
        return;
      }
      
      // ユーザー基本情報の更新データ
      const baseUserData = {
        display_name: editUserForm.displayName,
        bio: editUserForm.bio,
        location: editUserForm.location,
        website: editUserForm.website
      };
      
      // 新しくアップロードされた画像URLがある場合のみ送信する
      if (avatarPreview && avatarPreview !== userToEdit.avatar && !avatarPreview.startsWith('data:')) {
        // サーバーURLの場合は相対パスを抽出
        const avatarUrl = avatarPreview.startsWith(config.API_BASE_URL) 
          ? avatarPreview.replace(config.API_BASE_URL, '') 
          : avatarPreview;
        baseUserData.avatar = avatarUrl;
        console.log("アバター画像を更新します:", avatarUrl);
      }
      
      if (coverPreview && !coverPreview.startsWith('data:')) {
        // サーバーURLの場合は相対パスを抽出
        const coverUrl = coverPreview.startsWith(config.API_BASE_URL) 
          ? coverPreview.replace(config.API_BASE_URL, '') 
          : coverPreview;
        baseUserData.cover_image = coverUrl;
        console.log("カバー画像を更新します:", coverUrl);
      }
      
      // 管理者/企業ユーザー向けAPIリクエストのデバッグログ
      console.log('送信するデータ:', JSON.stringify(baseUserData, (key, value) => {
        // 画像データは長いので省略表示
        if (key === 'avatar' || key === 'cover_image') {
          return value ? value.substring(0, 50) + '...(省略)' : null;
        }
        return value;
      }));
      console.log('認証トークン:', token ? '有効' : '無効');
      
      
      // 管理者/企業ユーザー向けAPIリクエストを送信
      try {
        const response = await axios.put(`${config.API_BASE_URL}/api/users/admin/edit_user/${userToEdit.username}`, baseUserData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('API応答:', response.data);
      } catch (apiError) {
        console.error('API呼び出しエラー:', apiError);
        console.error('エラーレスポンス:', apiError.response?.data);
        throw apiError;
      }
      
      // 投稿ユーザー情報が存在し、タブが投稿ユーザー情報の場合
      if (isPosterUser) {
        // バリデーション
        if (posterProfile.age && !validateNumberField(posterProfile.age, 18, 100)) {
          setError('年齢は18歳以上で入力してください');
          setLoading(false);
          return;
        }
        
        if (posterProfile.height && !validateNumberField(posterProfile.height, 140, 200)) {
          setError('身長は140cm〜200cmの範囲で入力してください');
          setLoading(false);
          return;
        }
        
        if (posterProfile.bust && !validateNumberField(posterProfile.bust, 70, 150)) {
          setError('バストは70cm〜150cmの範囲で入力してください');
          setLoading(false);
          return;
        }
        
        if (posterProfile.waist && !validateNumberField(posterProfile.waist, 50, 120)) {
          setError('ウエストは50cm〜120cmの範囲で入力してください');
          setLoading(false);
          return;
        }
        
        if (posterProfile.hip && !validateNumberField(posterProfile.hip, 70, 150)) {
          setError('ヒップは70cm〜150cmの範囲で入力してください');
          setLoading(false);
          return;
        }
        
        // 空の文字列をnullに変換
        const cleanPosterProfile = {};
        Object.keys(posterProfile).forEach(key => {
          if (posterProfile[key] === '') {
            cleanPosterProfile[key] = null;
          } else {
            cleanPosterProfile[key] = posterProfile[key];
          }
        });
        
        
        
        console.log('投稿ユーザープロフィール送信データ:', cleanPosterProfile);
        
        // 管理者/企業ユーザー向けAPIリクエストを送信
        try {
          const posterResponse = await axios.put(`${config.API_BASE_URL}/api/users/admin/poster_profile/${userToEdit.username}`, cleanPosterProfile, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          console.log('投稿ユーザープロフィール更新応答:', posterResponse.data);
        } catch (posterApiError) {
          console.error('投稿ユーザープロフィール更新エラー:', posterApiError);
          console.error('エラーレスポンス:', posterApiError.response?.data);
          throw posterApiError;
        }
      }
      
      // 成功したらリストを更新
      const updatedUsers = taggedUsers.map(user => {
        if (user.id === userToEdit.id) {
          return {
            ...user,
            username: userToEdit.username, // usernameは変更不可
            displayName: editUserForm.displayName,
            avatar: avatarPreview // アバター画像も更新
          };
        }
        return user;
      });
      
      setTaggedUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      setEditUserDialogOpen(false);
      setLoading(false);
      
      // 成功メッセージを表示
      toast.success('ユーザー情報が更新されました', {
        position: 'top-center',
        autoClose: 3000
      });
    } catch (err) {
      console.error('ユーザー編集に失敗しました:', err);
      setError('ユーザーの編集中にエラーが発生しました。');
      setLoading(false);
      
      // エラーメッセージを表示
      toast.error(`ユーザー情報の更新に失敗しました: ${err.message || 'サーバーエラー'}`, {
        position: 'top-center',
        autoClose: 5000
      });
    }
  };

  // アバター画像の変更処理
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // ファイルサイズのチェック (500KB以下に制限)
      if (file.size > 512 * 1024) {
        toast.error('画像サイズが大きすぎます。500KB以下の画像を選択してください', {
          position: 'top-center',
          autoClose: 3000
        });
        return;
      }

      // まずファイルをプレビュー表示（base64）
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // 画像をサーバーにアップロード
      try {
        const uploadedUrl = await uploadImage(file, 'avatar');
        if (uploadedUrl) {
          // アップロード成功時はサーバーURLをプレビューに設定
          setAvatarPreview(`${config.API_BASE_URL}${uploadedUrl}`);
          console.log('アバター画像がアップロードされました:', uploadedUrl);
        }
      } catch (error) {
        console.error('アバター画像のアップロードに失敗しました:', error);
        toast.error('アバター画像のアップロードに失敗しました', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    }
  };

  // カバー画像の変更処理
  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // ファイルサイズのチェック (1MB以下に制限)
      if (file.size > 1024 * 1024) {
        toast.error('画像サイズが大きすぎます。1MB以下の画像を選択してください', {
          position: 'top-center',
          autoClose: 3000
        });
        return;
      }

      // まずファイルをプレビュー表示（base64）
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // 画像をサーバーにアップロード
      try {
        const uploadedUrl = await uploadImage(file, 'cover');
        if (uploadedUrl) {
          // アップロード成功時はサーバーURLをプレビューに設定
          setCoverPreview(`${config.API_BASE_URL}${uploadedUrl}`);
          console.log('カバー画像がアップロードされました:', uploadedUrl);
        }
      } catch (error) {
        console.error('カバー画像のアップロードに失敗しました:', error);
        toast.error('カバー画像のアップロードに失敗しました', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    }
  };

  // ユーザープロフィール表示ダイアログ
  const handleOpenViewDialog = async (user) => {
    setUserToView(user);
    setViewUserDialogOpen(true);
    setProfileLoading(true);
    
    try {
      // ユーザープロフィール情報を取得
      const response = await axios.get(`${config.API_BASE_URL}/api/users/${user.username}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setUserProfile(response.data);
      setProfileLoading(false);
    } catch (err) {
      console.error('ユーザープロフィールの取得に失敗しました:', err);
      
      // モック用のプロフィールデータ
      setUserProfile({
        id: user.id,
        username: user.username,
        display_name: user.displayName,
        email: user.email,
        avatar: user.avatar,
        bio: 'このユーザーの自己紹介文です。',
        location: '東京',
        created_at: user.addedAt
      });
      
      setProfileLoading(false);
    }
  };
  
  const handleCloseViewDialog = () => {
    setViewUserDialogOpen(false);
    setUserToView(null);
    setUserProfile(null);
  };
  
  // ユーザー削除処理
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };
  
  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // タグ付け関係を確認
      console.log(`削除対象ユーザー: ${userToDelete.username} (ID: ${userToDelete.id})`);
      const hasRelationship = await checkTagRelationship(userToDelete.username);
      
      if (!hasRelationship) {
        setError(`このユーザー(${userToDelete.username})を削除する権限がありません。タグ付け関係が見つかりません。`);
        setLoading(false);
        return;
      }
      
      // ユーザー削除APIを呼び出し
      await axios.delete(`${config.API_BASE_URL}/api/users/${userToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // 成功メッセージ
      toast.success(`ユーザー「${userToDelete.displayName}」を削除しました`, {
        position: 'top-center',
        autoClose: 3000
      });
      
      // ユーザーリストを更新
      setTaggedUsers(taggedUsers.filter(user => user.id !== userToDelete.id));
      setFilteredUsers(filteredUsers.filter(user => user.id !== userToDelete.id));
      
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setLoading(false);
    } catch (err) {
      console.error('ユーザー削除に失敗しました:', err);
      
      // エラーメッセージを詳細に表示
      const errorMessage = err.response?.data?.message || 'ユーザーの削除中にエラーが発生しました。';
      setError(errorMessage);
      
      toast.error(`ユーザー削除に失敗しました: ${errorMessage}`, {
        position: 'top-center',
        autoClose: 5000
      });
      
      setLoading(false);
    }
  };
  
  // 日付フォーマッター
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };
  
  // デバッグ用：currentUserの構造をコンソールに出力
  useEffect(() => {
    console.log('企業ユーザー情報:', currentUser);
    // オブジェクトのキーを調べる
    console.log('企業ユーザー情報のキー:', Object.keys(currentUser || {}));
    // 店舗情報を探すための詳細デバッグ
    if (currentUser) {
      console.log('shop_name:', currentUser.shop_name);
      console.log('prefecture:', currentUser.prefecture);
      console.log('display_name:', currentUser.display_name);
      
      // 都道府県情報の詳細デバッグ
      console.log('都道府県情報の詳細:', JSON.stringify(currentUser.prefecture, null, 2));
      
      // 深いネストを探索
      console.log('corporate_profile:', currentUser.corporate_profile);
      if (currentUser.corporate_profile) {
        console.log('corporate_profile.prefecture:', currentUser.corporate_profile.prefecture);
      }
      
      // 全構造を表示
      console.log('currentUser全構造:', JSON.stringify(currentUser, null, 2));
    }
  }, [currentUser]);
  
  // 都道府県一覧を取得
  const fetchPrefectures = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/prefectures`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setPrefectures(response.data || []);
    } catch (error) {
      
      // モックデータ
      setPrefectures([
        { code: '13', name: '東京都' },
        { code: '14', name: '神奈川県' },
        { code: '12', name: '千葉県' },
        { code: '11', name: '埼玉県' }
      ]);
    }
  };
  
  // 市区町村一覧を取得
  const fetchCities = async (prefectureCode) => {
    if (!prefectureCode) {
      setCities([]);
      return;
    }
    
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/cities/${prefectureCode}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCities(response.data || []);
    } catch (error) {
      
      // モックデータ
      setCities([
        { code: '13101', name: '千代田区' },
        { code: '13102', name: '中央区' },
        { code: '13103', name: '港区' }
      ]);
    }
  };
  
  // データ取得
  useEffect(() => {
    const fetchTaggedUsers = async () => {
      try {
        setLoading(true);
        
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/tagged-users`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const users = response.data.users || [];
        setTaggedUsers(users);
        setFilteredUsers(users);
        
        // フォローステータスを実際にAPIから確認
        const initialFollowStatus = {};
        for (const user of users) {
          try {
            // 各ユーザーのフォロー状態を確認
            const isFollowing = await checkFollowStatus(user.username);
            initialFollowStatus[user.id] = isFollowing;
          } catch (error) {
            
            initialFollowStatus[user.id] = false; // エラー時はフォローしていないと仮定
          }
        }
        setFollowStatus(initialFollowStatus);
        
        setLoading(false);
      } catch (err) {
        
        setError('データの読み込み中にエラーが発生しました。');
        setLoading(false);
        
        // モックデータを設定（開発用）
        const mockUsers = generateMockUsers();
        setTaggedUsers(mockUsers);
        setFilteredUsers(mockUsers);
        
        // モックのフォローステータス
        const mockFollowStatus = {};
        mockUsers.forEach(user => {
          mockFollowStatus[user.id] = Math.random() > 0.5;
        });
        setFollowStatus(mockFollowStatus);
      }
    };
    
    fetchTaggedUsers();
    fetchPrefectures(); // 都道府県データの取得
  }, [token]);
  
  if (loading && !taggedUsers.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="tagged-users-container">
      <Typography variant="h4" component="h1" gutterBottom>
        タグ付けユーザー一覧
      </Typography>
      
      <Paper className="tagged-users-paper">
        <Grid container spacing={2} alignItems="center" className="search-header">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="ユーザー名、メール、タグで検索"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} className="action-buttons">
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              component={Link}
              to="/poster-register"
              state={{ 
                username: '',
                shop_name: currentUser?.display_name || '',
                prefecture_code: '02' // 青森県のコードを固定値として設定
              }}
              onClick={() => {
                
                
              }}
            >
              投稿ユーザー登録
            </Button>
          </Grid>
        </Grid>
        
        {error && <Typography color="error" className="error-message">{error}</Typography>}
        
        <TableContainer className="users-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ユーザー</TableCell>
                <TableCell>タグ</TableCell>
                <TableCell>追加日</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar src={normalizeImageUrl(user.avatar)} alt={user.username} className="user-avatar" />
                        <Box ml={2}>
                          <Typography variant="subtitle2">{user.displayName}</Typography>
                          <Typography variant="body2" color="textSecondary">@{user.username}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className="tags-container">
                        {user.tags.map((tag, index) => (
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            className="user-tag"
                            icon={<TagIcon fontSize="small" />}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(user.addedAt)}</TableCell>
                    <TableCell>
                      <Box display="flex">
                        <Tooltip title="フォロー／フォロー解除">
                          <Button
                            variant={followStatus[user.id] ? "outlined" : "contained"}
                            color="primary"
                            size="small"
                            sx={{ mr: 1, minWidth: '80px', height: '30px', p: '2px 8px' }}
                            onClick={() => handleFollowToggle(user.id, user.username)}
                          >
                            {followStatus[user.id] ? 'フォロー中' : 'フォロー'}
                          </Button>
                        </Tooltip>
                        <Tooltip title="プロフィールを表示">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenViewDialog(user)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="プロフィールを編集">
                          <IconButton 
                            color="secondary" 
                            size="small"
                            onClick={() => handleOpenEditDialog(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ユーザーを削除">
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={() => handleOpenDeleteDialog(user)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`}
        />
      </Paper>
      
      {/* ユーザータグ付けダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseTagDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          ユーザーをタグ付け
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="ユーザー名"
                name="username"
                value={newUserFormData.username}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="メールアドレス"
                name="email"
                type="email"
                value={newUserFormData.email}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                タグ
              </Typography>
              <Box display="flex" alignItems="center">
                <TextField
                  label="タグを追加"
                  value={inputTag}
                  onChange={handleInputTagChange}
                  fullWidth
                />
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleAddTag}
                  sx={{ ml: 1, height: 56 }}
                >
                  追加
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className="tags-preview">
                {newUserFormData.tags.length > 0 ? (
                  newUserFormData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      className="tag-chip"
                      color="primary"
                      size="medium"
                    />
                  ))
                ) : (
                  <Typography color="textSecondary">
                    タグが追加されていません
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagDialog} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleSubmitNewUser}
            variant="contained"
            color="primary"
            disabled={!newUserFormData.username || !newUserFormData.email || newUserFormData.tags.length === 0}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ユーザー削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          ユーザーを完全に削除しますか？
        </DialogTitle>
        <DialogContent>
          <Typography>
            <strong>警告：</strong> ユーザー「{userToDelete?.displayName}」(@{userToDelete?.username})を完全に削除しようとしています。
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            この操作を実行すると、ユーザーアカウント、プロフィール、投稿、コメントなど、このユーザーに関連するすべてのデータが完全に削除されます。
            この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            キャンセル
          </Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            完全に削除する
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ユーザープロフィール編集ダイアログ */}
      <Dialog open={editUserDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          ユーザープロフィール編集
        </DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : userToEdit && (
            <>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{ mb: 2 }}
              >
                <Tab label="基本情報" />
                {isPosterUser && <Tab label="投稿ユーザー情報" />}
              </Tabs>
              
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  {/* カバー画像 */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>カバー画像</Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        backgroundColor: '#f0f0f0',
                        backgroundImage: coverPreview ? `url(${coverPreview})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: 1,
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <Box 
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          right: 10
                        }}
                      >
                        <input
                          accept="image/*"
                          id="cover-upload"
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleCoverChange}
                        />
                        <label htmlFor="cover-upload">
                          <Button 
                            variant="contained" 
                            color="primary"
                            component="span"
                            size="small"
                          >
                            変更
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                    <Box sx={{ position: 'relative', mb: 1 }}>
                      <Avatar 
                        src={avatarPreview} 
                        alt={userToEdit.displayName}
                        sx={{ width: 100, height: 100 }}
                      />
                      <Box sx={{ position: 'absolute', bottom: 0, right: -10 }}>
                        <input
                          accept="image/*"
                          id="avatar-upload"
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleAvatarChange}
                        />
                        <label htmlFor="avatar-upload">
                          <IconButton 
                            color="primary" 
                            component="span"
                            sx={{ backgroundColor: 'white' }}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </label>
                      </Box>
                    </Box>
                    <Typography variant="caption">プロフィール画像</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="ユーザー名"
                      name="username"
                      value={editUserForm.username}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="表示名"
                      name="displayName"
                      value={editUserForm.displayName}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="メールアドレス"
                      name="email"
                      type="email"
                      value={editUserForm.email}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="自己紹介"
                      name="bio"
                      value={editUserForm.bio}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                      multiline
                      rows={3}
                      inputProps={{ maxLength: 160 }}
                      helperText={`${editUserForm.bio ? editUserForm.bio.length : 0}/160`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="場所"
                      name="location"
                      value={editUserForm.location}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="ウェブサイト"
                      name="website"
                      value={editUserForm.website}
                      onChange={handleEditFormChange}
                      fullWidth
                      margin="normal"
                      placeholder="https://"
                    />
                  </Grid>
                </Grid>
              )}
              
              {activeTab === 1 && isPosterUser && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="店名"
                      name="shop_name"
                      value={posterProfile.shop_name}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      helperText="投稿時に固定タグとして設定されます"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>都道府県</InputLabel>
                      <Select
                        name="prefecture_code"
                        value={posterProfile.prefecture_code}
                        onChange={handlePosterInputChange}
                        label="都道府県"
                      >
                        <MenuItem value="">選択なし</MenuItem>
                        {prefectures.map(prefecture => (
                          <MenuItem key={prefecture.code} value={prefecture.code}>
                            {prefecture.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>都道府県を選択してください</FormHelperText>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal" disabled={!posterProfile.prefecture_code}>
                      <InputLabel>市区町村</InputLabel>
                      <Select
                        name="city_code"
                        value={posterProfile.city_code}
                        onChange={handlePosterInputChange}
                        label="市区町村"
                      >
                        <MenuItem value="">選択なし</MenuItem>
                        {cities.map(city => (
                          <MenuItem key={city.code} value={city.code}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>市区町村を選択してください</FormHelperText>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="源氏名"
                      name="stage_name"
                      value={posterProfile.stage_name}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      helperText="源氏名を入力してください"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="年齢"
                      name="age"
                      type="number"
                      value={posterProfile.age}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 18, max: 100 } }}
                      helperText="18歳以上で入力してください"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="身長 (cm)"
                      name="height"
                      type="number"
                      value={posterProfile.height}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 140, max: 200 } }}
                      helperText="140cm〜200cmで入力してください"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="バスト (cm)"
                      name="bust"
                      type="number"
                      value={posterProfile.bust}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 70, max: 150 } }}
                      helperText="70cm〜150cmで入力してください"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>カップ数</InputLabel>
                      <Select
                        name="cup_size"
                        value={posterProfile.cup_size}
                        onChange={handlePosterInputChange}
                        label="カップ数"
                      >
                        <MenuItem value="">選択なし</MenuItem>
                        {CUP_SIZES.map(size => (
                          <MenuItem key={size} value={size}>
                            {size}カップ
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>カップ数を選択してください</FormHelperText>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="ウエスト (cm)"
                      name="waist"
                      type="number"
                      value={posterProfile.waist}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 50, max: 120 } }}
                      helperText="50cm〜120cmで入力してください"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="ヒップ (cm)"
                      name="hip"
                      type="number"
                      value={posterProfile.hip}
                      onChange={handlePosterInputChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 70, max: 150 } }}
                      helperText="70cm〜150cmで入力してください"
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {error && (
            <Box mt={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleSubmitEditUser}
            variant="contained"
            color="primary"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ユーザープロフィール表示ダイアログ */}
      <Dialog open={viewUserDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          ユーザープロフィール
        </DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : userProfile ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Avatar 
                    src={userProfile.avatar} 
                    alt={userProfile.display_name || userProfile.username}
                    sx={{ width: 120, height: 120 }}
                  />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="h5">{userProfile.display_name}</Typography>
                  <Typography variant="body1" color="textSecondary">@{userProfile.username}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                
                {userProfile.bio && (
                  <Grid item xs={12}>
                    <Typography variant="body1">{userProfile.bio}</Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mt={1}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      登録日: {formatDate(userProfile.created_at)}
                    </Typography>
                  </Box>
                  
                  {userProfile.location && (
                    <Box display="flex" alignItems="center" mt={1}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {userProfile.location}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2 }}>タグ</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {userToView?.tags.map((tag, index) => (
                      <Chip 
                        key={index} 
                        label={tag} 
                        color="primary" 
                        variant="outlined" 
                        icon={<TagIcon fontSize="small" />}
                      />
                    ))}
                  </Box>
                </Grid>
                
                {userProfile.poster_profile && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>投稿ユーザー情報</Typography>
                    <Grid container spacing={2} mt={1}>
                      {userProfile.poster_profile.shop_name && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2">所属店舗</Typography>
                          <Typography variant="body2">{userProfile.poster_profile.shop_name}</Typography>
                        </Grid>
                      )}
                      
                      {userProfile.poster_profile.stage_name && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2">スタッフ名</Typography>
                          <Typography variant="body2">{userProfile.poster_profile.stage_name}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography>ユーザー情報が見つかりませんでした。</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleOpenEditDialog(userToView)}
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
          >
            編集
          </Button>
          <Button onClick={handleCloseViewDialog} variant="contained" color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaggedUsers; 